import { IcoBolt, IcoBug, IcoClock, IcoCog, IcoFilter, IcoGauge, IcoGrid, IcoLayers, IcoPlug } from '../bz/icons';
import { ODD_STAGE_CHOICES, oddStageOf } from '../../helpers/oddStage';

// The Super Admin workspace registry: the sections, their order, groups and
// copy. Same idea as PlatformSettings' SECTIONS and BugZeroSidebar's
// PLATFORM_NAV. The sidebar and the breadcrumb both read from here.

export const SECTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    group: 'Super Admin',
    icon: 'home',
    blurb: 'What is on for this tenant, and what needs a look.'
  },
  {
    id: 'products',
    label: 'Products & features',
    group: 'Access',
    icon: 'box',
    blurb: 'Which products and product features this tenant can use.'
  },
  {
    id: 'vendors',
    label: 'Vendors',
    group: 'Data sources',
    icon: 'plug',
    blurb: 'Vendor integrations, plus the status.io health-check bypass for vendors that have one.'
  },
  {
    id: 'odd-vendors',
    label: 'ODD vendors',
    group: 'Data sources',
    icon: 'database',
    blurb: 'Vendors that come only from the ODD feed.'
  },
  {
    id: 'environment',
    label: 'ODD environment',
    group: 'Environment',
    icon: 'globe',
    blurb: 'Which ODD stage Prevent and Plan read from.'
  },
  {
    id: 'inventory',
    label: 'Inventory cleanup',
    group: 'Environment',
    icon: 'trash',
    blurb: 'How long missing managed products are kept, and whether they are deleted.'
  },
  {
    id: 'tasks',
    label: 'Tasks',
    group: 'Operations',
    icon: 'clock',
    blurb: 'Scheduled jobs you can run now, with when each one runs and when it last ran.'
  },
  {
    id: 'dashboard',
    label: 'Dashboard sections',
    group: 'Display',
    icon: 'layout',
    blurb: 'Panels shown on the BugZero dashboard.'
  },
  {
    id: 'advanced',
    label: 'All tenant settings',
    group: 'Advanced',
    icon: 'sliders',
    blurb: 'Every tenantSettings row, including rows no section claims yet.'
  }
];
export const SECTION_BY_ID = Object.fromEntries(SECTIONS.map(s => [s.id, s]));

// Prevent's (bugsProd) and Plan's (bsaProd) ODD stage, a choice of prod, dev or
// dev2 (STRY0039057). Rows written before it hold a boolean, which oddStageOf
// reads as prod (true) or dev (false); buildItems hands the views the stage.
const oddSource = (product, label, description) => ({
  section: 'environment',
  group: 'ODD source',
  typeLabel: 'Environment',
  product,
  label,
  logLabel: `${label} ODD source`,
  description,
  choices: ODD_STAGE_CHOICES,
  confirm: next =>
    oddStageOf(next) === 'prod' ? null : `${label} reads ODD ${oddStageOf(next)} for every user on this tenant.`,
  attention: v => (oddStageOf(v) === 'prod' ? null : `${label} reads ODD ${oddStageOf(v)}`)
});

// tenantSettings rows -> where and how they render. A row missing here still
// renders (under All tenant settings, flagged "Unmapped"), so a new migration
// never hides a setting.
//
// `source`/`apiKey` mark the rows that are not written through the generic
// tenant-settings endpoint: the three product flags go through the products
// API and bug coverage through the features API. They are also the rows the
// backend gives a default when they are missing: on, except Predict, which
// starts off (STRY0038309; see buildItems).
export const TENANT_SETTING_META = {
  planEnabled: {
    section: 'products',
    group: 'Products',
    typeLabel: 'Product',
    product: 'plan',
    source: 'product',
    apiKey: 'plan',
    label: 'Plan',
    description: 'Fleet inventory, lifecycle and upgrade planning.',
    confirm: next => (next ? null : 'Every user on this tenant loses access to Plan. Its data is kept.')
  },
  preventEnabled: {
    section: 'products',
    group: 'Products',
    typeLabel: 'Product',
    product: 'prevent',
    source: 'product',
    apiKey: 'prevent',
    label: 'Prevent',
    description: 'Bug intelligence, vendor bugs and the Prevent dashboard.',
    confirm: next => (next ? null : 'Every user on this tenant loses access to Prevent. Its data is kept.')
  },
  predictEnabled: {
    section: 'products',
    group: 'Products',
    typeLabel: 'Product',
    product: 'predict',
    source: 'product',
    apiKey: 'predict',
    label: 'Predict',
    description: 'The watch list of new bugs likely to become CVEs. Off until turned on here.',
    confirm: next => (next ? null : 'Every user on this tenant loses access to Predict. Its data is kept.')
  },
  bugCoverageEnabled: {
    section: 'products',
    group: 'Features',
    typeLabel: 'Feature',
    product: 'prevent',
    source: 'feature',
    apiKey: 'bugCoverage',
    label: 'Bug coverage',
    description: 'Published, open and addressed bug counts on the Prevent dashboard and vendor configuration pages.'
  },
  bsaEnabled: {
    section: 'products',
    group: 'Features',
    typeLabel: 'Feature',
    product: 'plan',
    label: 'Bug Scrub Advisor (BSA)',
    description: 'AI-assisted bug analysis and recommendations.'
  },
  bugZeroApiEnabled: {
    section: 'products',
    group: 'Features',
    typeLabel: 'Feature',
    product: 'platform',
    label: 'BugZero API',
    description: 'Lets this tenant create API keys and call the public BugZero API.',
    confirm: next => (next ? null : 'Turns off BugZero API access for this tenant.')
  },
  bsaProd: oddSource(
    'plan',
    'Plan',
    'The ODD stage Plan projects, inventory and exports read. Leave on Production outside testing.'
  ),
  bugsProd: oddSource(
    'prevent',
    'Prevent',
    'The ODD stage vendor runs and published bug counts read. Leave on Production outside testing.'
  ),
  managedProductMissingGracePeriodDays: {
    section: 'inventory',
    group: 'Missing products',
    typeLabel: 'Inventory',
    unit: 'days',
    min: 0,
    max: 365,
    label: 'Grace period',
    description:
      'Days a managed product can be missing from vendor syncs before it counts as gone. 0 counts it gone on the first sync that misses it.'
  },
  managedProductMissingDeleteEnabled: {
    section: 'inventory',
    group: 'Missing products',
    typeLabel: 'Inventory',
    label: 'Auto-delete missing products',
    description: 'Delete managed products that stay missing past the grace period.',
    confirm: next =>
      next ? 'Managed products missing longer than the grace period are permanently deleted on the next sync.' : null
  }
};

const DASHBOARD_META = {
  displayIntegration: 'Integration status tiles on the dashboard.',
  displayVendorIntegration: 'Per-vendor integration cards.',
  displayTenantBugStats: "Bug counts for this tenant's fleet.",
  displayOddBugStats: 'Bug counts from the ODD feed across all vendors.'
};

// Vendors that are configured elsewhere and never listed here.
const HIDDEN_VENDORS = new Set(['servicenow']); // lives under Platform → Integrations

// Rows the products and features APIs give a default when they are missing from
// tenantSettings. Shown with that default, not hidden: on, except the ones below.
const DEFAULT_ON_IDS = Object.keys(TENANT_SETTING_META).filter(id => TENANT_SETTING_META[id].apiKey);
// Predict starts off for every tenant until a Super Admin turns it on (STRY0038309).
const DEFAULT_OFF_IDS = new Set(['predictEnabled']);

// Which control a tenantSettings row gets, from the type it is stored with. Only
// boolean and number rows are editable: a switch writes { value: <bool>, type } and
// would silently turn any other row into a boolean. Everything else is shown as text.
// A row whose meta lists `choices` is a choice whatever its stored type, and writes
// { value: <choice>, type: 'string' }.
const KIND_BY_TYPE = { boolean: 'switch', number: 'number' };
const kindOf = type => KIND_BY_TYPE[type] || 'readonly';

// Global vendors (bugzeroapi, openai) are never switched on or off from Super Admin
// and are not counted as enabled or disabled. They are listed only for their status.io
// bypass, so one without a status.io page has nothing to show here.
export const isGlobalVendor = item => item.source === 'vendor' && item.vendor?.type === 'global';

// Normalise every store into one flat list of controls. Views only ever see
// these items, so a layout change never touches the data plumbing.
export function buildItems(state) {
  const items = [];

  const stored = new Set(state.tenantSettings.map(r => r.id));
  const missing = DEFAULT_ON_IDS.filter(id => !stored.has(id)).map(id => ({
    id,
    value: { type: 'boolean', value: !DEFAULT_OFF_IDS.has(id) },
    updatedAt: undefined
  }));

  for (const r of [...state.tenantSettings, ...missing]) {
    const meta = TENANT_SETTING_META[r.id];
    const choices = meta?.choices;
    const type = choices ? 'string' : r.value?.type;
    const value = choices ? oddStageOf(r.value?.value) : r.value?.value;
    items.push({
      uid: `ts:${r.id}`,
      source: meta?.source || 'tenantSetting',
      apiKey: meta?.apiKey,
      id: r.id,
      section: meta?.section || 'advanced',
      group: meta?.group || 'Unmapped',
      typeLabel: meta?.typeLabel || 'Unmapped',
      label: meta?.label || r.label,
      logLabel: meta?.logLabel,
      description: meta?.description || '',
      rawLabel: r.label,
      keyLabel: r.id,
      kind: choices ? 'choice' : kindOf(type),
      valueType: type,
      value,
      choices,
      confirm: meta?.confirm,
      product: meta?.product,
      unit: meta?.unit,
      min: meta?.min,
      max: meta?.max,
      registered: Boolean(meta),
      attention: meta?.attention ? meta.attention(value) : null,
      updatedAt: r.updatedAt
    });
  }

  for (const v of state.vendors) {
    if (HIDDEN_VENDORS.has(v.id)) continue;
    if (v.type === 'global' && !v.vendorData?.statusIoConfig) continue;
    const odd = v.type === 'odd-vendor';
    const section = odd ? 'odd-vendors' : 'vendors';
    const family = v.vendorFamily || null;
    const kind = odd ? 'ODD-only vendor' : v.type === 'global' ? 'Global vendor' : 'Vendor integration';
    items.push({
      uid: `vendor:${v.id}`,
      source: 'vendor',
      id: v.id,
      section,
      group: odd ? 'ODD vendors' : 'Vendors',
      typeLabel: odd ? 'ODD vendor' : 'Vendor',
      label: v.name,
      description: family ? `${kind} · ${family} family` : kind,
      keyLabel: `vendors/${v.id}.active`,
      kind: 'switch',
      value: Boolean(v.active),
      family,
      vendor: v,
      registered: true,
      confirm: next => (next ? null : `${v.name} disappears from the navbar for everyone on this tenant.`),
      attention: null,
      updatedAt: v.updatedAt
    });
    if (v.vendorData?.statusIoConfig) {
      const on = v.vendorData.bypassStatusIoCheck === true;
      items.push({
        uid: `bypass:${v.id}`,
        rowUid: `vendor:${v.id}`,
        source: 'bypass',
        id: v.id,
        section,
        group: 'status.io bypass',
        typeLabel: 'Bypass',
        label: `${v.name} · skip status.io check`,
        logLabel: `${v.name} status.io bypass`,
        description: 'Runs go ahead even when status.io reports an incident for this vendor.',
        keyLabel: `vendors/${v.id}.vendorData.bypassStatusIoCheck`,
        kind: 'switch',
        tone: 'warn',
        value: on,
        family,
        vendor: v,
        registered: true,
        confirm: next => (next ? `${v.name} runs will ignore status.io incidents until someone turns this off.` : null),
        attention: on ? 'status.io checks skipped' : null,
        updatedAt: v.updatedAt
      });
    }
  }

  for (const s of state.dashboardSections) {
    items.push({
      uid: `dash:${s.value}`,
      source: 'dashboard',
      id: s.value,
      section: 'dashboard',
      group: 'Panels',
      typeLabel: 'Dashboard',
      label: s.label,
      description: DASHBOARD_META[s.value] || '',
      keyLabel: `settings/bugzero.display.${s.value}`,
      kind: 'switch',
      value: Boolean(s.active),
      registered: true,
      attention: null,
      updatedAt: s.updatedAt
    });
  }

  // Tasks come from GET /super-admin/tasks, the backend's task registry, so a new
  // task needs no code here. `value` is whether a run this page started is still
  // being waited on.
  for (const t of state.tasks || []) {
    items.push({
      uid: `task:${t.id}`,
      source: 'task',
      id: t.id,
      section: 'tasks',
      group: 'Scheduled jobs',
      typeLabel: 'Task',
      label: t.label,
      description: t.description || '',
      keyLabel: t.functionName,
      kind: 'action',
      value: Boolean(t.running),
      schedule: t.schedule?.label || t.schedule?.expression || '',
      lastRunAt: t.lastRunAt || null,
      registered: true,
      attention: null,
      updatedAt: undefined
    });
  }

  return items.map(i => ({
    ...i,
    hay: [i.label, i.description, i.keyLabel, i.group, i.family, i.rawLabel, SECTION_BY_ID[i.section]?.label]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
  }));
}

// Every whitespace-separated term must appear; a blank or whitespace-only query is no query.
export const matches = (i, q) =>
  (q || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every(t => i.hay.includes(t));

export function fmtValue(item, v) {
  if (v === undefined) return '—';
  if (item.kind === 'readonly') return v === null || v === '' ? '—' : typeof v === 'string' ? v : JSON.stringify(v);
  if (item.kind === 'number') return `${v}${item.unit ? ` ${item.unit}` : ''}`;
  if (item.kind === 'choice') return item.choices.find(c => c.value === v)?.label ?? String(v);
  return v ? 'On' : 'Off';
}

// Sidebar entries for the Super Admin context, in BugZeroSidebar's own item
// shape ({ id, label, Icon, to }), one SectionLabel per group.
const NAV_ICON = {
  overview: IcoGrid,
  products: IcoLayers,
  vendors: IcoPlug,
  'odd-vendors': IcoBug,
  environment: IcoBolt,
  inventory: IcoFilter,
  tasks: IcoClock,
  dashboard: IcoGauge,
  advanced: IcoCog
};
export const SUPER_ADMIN_NAV = [...new Set(SECTIONS.map(s => s.group))].map(label => ({
  label,
  items: SECTIONS.filter(s => s.group === label).map(s => ({
    id: s.id,
    label: s.label,
    Icon: NAV_ICON[s.id],
    to: s.id === 'overview' ? '/super-admin' : `/super-admin/${s.id}`
  }))
}));
